import { CareerChallengeParticipantsTabComponent } from './features/admin/career-challenges/tabs/participants-tab.component';
import { Routes } from '@angular/router';
import {
  adminGuard,
  authGuard,
  defaultLandingRedirect,
  guestGuard,
  portalGuard,
} from './core/auth.guard';
import { learnerAuthGuard } from './core/learner-auth.guard';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { AdminDashboardComponent } from './features/admin/admin-dashboard/admin-dashboard.component';
import { ForgotPasswordPageComponent } from './features/auth/forgot-password-page/forgot-password-page.component';
import { LoginPageComponent } from './features/auth/login-page/login-page.component';
import { ResetPasswordPageComponent } from './features/auth/reset-password-page/reset-password-page.component';
import { CareerRoleAdminShellComponent } from './features/admin/career-roles/career-role-admin-shell/career-role-admin-shell.component';
import { CareerRoleListViewComponent } from './features/admin/career-roles/career-role-list-view/career-role-list-view.component';
import { CareerRoleDetailComponent } from './features/admin/career-roles/career-role-detail/career-role-detail.component';
import { OverviewTabComponent } from './features/admin/career-roles/tabs/overview-tab.component';
import { SkillsResourcesTabComponent } from './features/admin/career-roles/tabs/skills-resources-tab.component';
import { TopicEvidenceModesTabComponent } from './features/admin/career-roles/tabs/topic-evidence-modes-tab.component';
import { ResourcesRevalidateTabComponent } from './features/admin/career-roles/tabs/resources-revalidate-tab.component';
import { CareerRoleGraphTabComponent } from './features/admin/career-roles/tabs/career-role-graph-tab.component';
import { CareerChallengeAdminShellComponent } from './features/admin/career-challenges/career-challenge-admin-shell/career-challenge-admin-shell.component';
import { CareerChallengeListComponent } from './features/admin/career-challenges/career-challenge-list/career-challenge-list.component';
import { CareerChallengeDetailComponent } from './features/admin/career-challenges/career-challenge-detail/career-challenge-detail.component';
import { CareerChallengeOverviewTabComponent } from './features/admin/career-challenges/tabs/overview-tab.component';
import { CareerChallengeSponsorsTabComponent } from './features/admin/career-challenges/tabs/sponsors-tab.component';
import { CareerChallengeJudgesTabComponent } from './features/admin/career-challenges/tabs/judges-tab.component';
import { CareerChallengePrizesTabComponent } from './features/admin/career-challenges/tabs/prizes-tab.component';
import { CareerChallengeTeamsTabComponent } from './features/admin/career-challenges/tabs/teams-tab.component';
import { CareerChallengeSubmissionsTabComponent } from './features/admin/career-challenges/tabs/submissions-tab.component';
import { CareerChallengeScheduleTabComponent } from './features/admin/career-challenges/tabs/schedule-tab.component';
import { CareerChallengePreviewTabComponent } from './features/admin/career-challenges/tabs/preview-tab.component';

export const routes: Routes = [
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: '', pathMatch: 'full', redirectTo: defaultLandingRedirect },
      {
        path: 'design-preview',
        loadComponent: () =>
          import('./features/design-preview/design-preview.component').then(
            (m) => m.DesignPreviewComponent,
          ),
      },
      {
        path: 'login',
        component: LoginPageComponent,
        canActivate: [guestGuard],
      },
      {
        path: 'partner/login',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/auth/partner-login-page/partner-login-page.component').then(
            (m) => m.PartnerLoginPageComponent,
          ),
      },
      { path: 'forgot-password', component: ForgotPasswordPageComponent },
      { path: 'reset-password', component: ResetPasswordPageComponent },
      {
        path: 'portal',
        canActivate: [portalGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/portal/portal-hub-page.component').then(
                (m) => m.PortalHubPageComponent,
              ),
          },
          {
            path: 'referral',
            loadComponent: () =>
              import('./features/portal/portal-referral-page.component').then(
                (m) => m.PortalReferralPageComponent,
              ),
          },
          {
            path: 'partner',
            loadComponent: () =>
              import('./features/portal/portal-partner-page.component').then(
                (m) => m.PortalPartnerPageComponent,
              ),
          },
          {
            path: 'partner/learners/:learnerId',
            loadComponent: () =>
              import('./features/portal/portal-partner-learner-detail-page.component').then(
                (m) => m.PortalPartnerLearnerDetailPageComponent,
              ),
          },
        ],
      },
      {
        path: 'learner/login',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/learner/auth/learner-login-page.component').then(
            (m) => m.LearnerLoginPageComponent,
          ),
      },
      {
        path: 'learner/legal',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/legal/learner-legal-acceptance-page.component').then(
            (m) => m.LearnerLegalAcceptancePageComponent,
          ),
      },
      {
        path: 'auth/handoff',
        loadComponent: () =>
          import('./features/learner/auth/auth-handoff-page.component').then(
            (m) => m.AuthHandoffPageComponent,
          ),
      },
      {
        path: 'share/:token',
        loadComponent: () =>
          import('./features/public/shared-profile/shared-profile.component').then(
            (m) => m.SharedProfileComponent,
          ),
      },
      {
        path: 'share/profile/:token',
        loadComponent: () =>
          import('./features/public/shared-profile/shared-profile.component').then(
            (m) => m.SharedProfileComponent,
          ),
      },
      {
        path: 'journey',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/journey/my-journey-page.component').then(
            (m) => m.MyJourneyPageComponent,
          ),
      },
      {
        path: 'onboarding/role-decision',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/journey/target-role-decision-page.component').then(
            (m) => m.TargetRoleDecisionPageComponent,
          ),
      },
      {
        path: 'onboarding/career-counselling',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/journey/career-counselling-page.component').then(
            (m) => m.CareerCounsellingPageComponent,
          ),
      },
      {
        path: 'onboarding/career-matches/:sessionId',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/journey/career-matches-page.component').then(
            (m) => m.CareerMatchesPageComponent,
          ),
      },
      {
        path: 'p/:token',
        loadComponent: () =>
          import('./features/public/shared-profile/shared-profile.component').then(
            (m) => m.SharedProfileComponent,
          ),
      },
      {
        path: 'learning',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learner-learning-page.component').then(
            (m) => m.LearnerLearningPageComponent,
          ),
      },
      {
        path: 'learning/plans/:planId',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learner-learning-page.component').then(
            (m) => m.LearnerLearningPageComponent,
          ),
      },
      {
        path: 'learning/continue',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learning-continue-page.component').then(
            (m) => m.LearningContinuePageComponent,
          ),
      },
      {
        path: 'learning/week/:weekNumber/processing',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learning-week-processing-page.component').then(
            (m) => m.LearningWeekProcessingPageComponent,
          ),
      },
      {
        path: 'learning/week/:weekNumber/reassessment',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learning-week-reassessment-page.component').then(
            (m) => m.LearningWeekReassessmentPageComponent,
          ),
      },
      {
        path: 'learning/week/:weekNumber/reassessment/chat',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learning-week-reassessment-chat-page.component').then(
            (m) => m.LearningWeekReassessmentChatPageComponent,
          ),
      },
      {
        path: 'learning/week/:weekNumber/reassessment/processing',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learning-week-reassessment-processing-page.component').then(
            (m) => m.LearningWeekReassessmentProcessingPageComponent,
          ),
      },
      {
        path: 'learning/week/:weekNumber/summary',
        redirectTo: 'learning/week/:weekNumber',
      },
      {
        path: 'learning/week/:weekNumber',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learning-week-detail-page.component').then(
            (m) => m.LearningWeekDetailPageComponent,
          ),
      },
      {
        path: 'learning/plans/:planId/week/:weekNumber',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learning-week-detail-page.component').then(
            (m) => m.LearningWeekDetailPageComponent,
          ),
      },
      {
        // Legacy completion URL → combined plan overview on /learning/plans/:planId
        path: 'learning/plans/:planId/completion',
        redirectTo: '/learning/plans/:planId',
        pathMatch: 'full',
      },
      {
        path: 'learning/lessons/:lessonId',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/learning/learner-canonical-lesson-page.component').then(
            (m) => m.LearnerCanonicalLessonPageComponent,
          ),
      },
      {
        path: 'career-challenges',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-challenges/career-challenges-hub-page.component').then(
            (m) => m.CareerChallengesHubPageComponent,
          ),
      },
      {
        path: 'career-challenges/:id',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-challenges/career-challenge-detail-page.component').then(
            (m) => m.CareerChallengeDetailPageComponent,
          ),
      },
      {
        path: 'career-challenges/:id/team',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-challenges/career-challenge-team-page.component').then(
            (m) => m.CareerChallengeTeamPageComponent,
          ),
      },
      {
        path: 'career-challenges/:id/submit',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-challenges/career-challenge-submit-page.component').then(
            (m) => m.CareerChallengeSubmitPageComponent,
          ),
      },
      {
        path: 'career-challenges/:id/comprehension',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-challenges/career-challenge-comprehension-page.component').then(
            (m) => m.CareerChallengeComprehensionPageComponent,
          ),
      },
      {
        path: 'career-challenges/:id/comprehension/processing',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-challenges/career-challenge-comprehension-processing-page.component').then(
            (m) => m.CareerChallengeComprehensionProcessingPageComponent,
          ),
      },
      {
        path: 'career-challenges/:id/leaderboard',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-challenges/career-challenge-leaderboard-page.component').then(
            (m) => m.CareerChallengeLeaderboardPageComponent,
          ),
      },
      {
        path: 'notifications',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/notifications/learner-notifications-page.component').then(
            (m) => m.LearnerNotificationsPageComponent,
          ),
      },
      {
        path: 'achievements',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/recognition/achievements-hub-page.component').then(
            (m) => m.AchievementsHubPageComponent,
          ),
      },
      {
        path: 'achievements/:id',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/recognition/achievement-detail-page.component').then(
            (m) => m.AchievementDetailPageComponent,
          ),
      },
      {
        path: 'job-readiness',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/recognition/job-readiness-page.component').then(
            (m) => m.JobReadinessPageComponent,
          ),
      },
      {
        path: 'community',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/community/community-hub-page.component').then(
            (m) => m.CommunityHubPageComponent,
          ),
      },
      {
        path: 'community/leaderboards',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/community/community-leaderboards-page.component').then(
            (m) => m.CommunityLeaderboardsPageComponent,
          ),
      },
      {
        path: 'account/complete-profile',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/account/learner-complete-profile-page.component').then(
            (m) => m.LearnerCompleteProfilePageComponent,
          ),
      },
      {
        path: 'onboarding/target-role',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/onboarding/onboarding-target-role-page.component').then(
            (m) => m.OnboardingTargetRolePageComponent,
          ),
      },
      {
        path: 'onboarding/career-roles/:slug/required-skills',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/career-roles/career-role-required-skills-page.component').then(
            (m) => m.CareerRoleRequiredSkillsPageComponent,
          ),
      },
      {
        path: 'onboarding/evaluation-unavailable',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/onboarding/onboarding-evaluation-unavailable-page.component').then(
            (m) => m.OnboardingEvaluationUnavailablePageComponent,
          ),
      },
      {
        path: 'onboarding/start-direct',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/onboarding/onboarding-start-direct-page.component').then(
            (m) => m.OnboardingStartDirectPageComponent,
          ),
      },
      // {
      //   path: 'onboarding/gap-summary',
      //   canActivate: [learnerAuthGuard],
      //   loadComponent: () =>
      //     import('./features/learner/onboarding/onboarding-gap-summary-page.component').then(
      //       (m) => m.OnboardingGapSummaryPageComponent
      //     )
      // },
      {
        path: 'onboarding/pack-payment',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/payment/learner-pack-payment-page.component').then(
            (m) => m.LearnerPackPaymentPageComponent,
          ),
      },
      {
        path: 'onboarding/career-path-payment',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/payment/learner-career-path-payment-page.component').then(
            (m) => m.LearnerCareerPathPaymentPageComponent,
          ),
      },
      {
        path: 'onboarding/plan-preview',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/onboarding/onboarding-plan-preview-page.component').then(
            (m) => m.OnboardingPlanPreviewPageComponent,
          ),
      },
      {
        path: 'onboarding/plan-creating',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/onboarding/onboarding-plan-creating-page.component').then(
            (m) => m.OnboardingPlanCreatingPageComponent,
          ),
      },
      {
        path: 'onboarding/plan-failed',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/onboarding/onboarding-plan-failed-page.component').then(
            (m) => m.OnboardingPlanFailedPageComponent,
          ),
      },
      {
        path: 'account/profile',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/account/learner-account-page.component').then(
            (m) => m.LearnerAccountPageComponent,
          ),
      },
      {
        path: 'account/learning-plans',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/account/my-learning-plans-page.component').then(
            (m) => m.MyLearningPlansPageComponent,
          ),
      },
      {
        path: 'account/challenges',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/account/my-code-challenges-page.component').then(
            (m) => m.MyCodeChallengesPageComponent,
          ),
      },
      {
        path: 'account/shares',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/recognition/my-shares-page.component').then(
            (m) => m.MySharesPageComponent,
          ),
      },
      {
        path: 'account/skills',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/recognition/my-skills-page.component').then(
            (m) => m.MySkillsPageComponent,
          ),
      },
      {
        path: 'account/delete',
        canActivate: [learnerAuthGuard],
        loadComponent: () =>
          import('./features/learner/account/delete-account-page.component').then(
            (m) => m.DeleteAccountPageComponent,
          ),
      },
      {
        path: 'account/deleted',
        loadComponent: () =>
          import('./features/learner/account/account-deleted-page.component').then(
            (m) => m.AccountDeletedPageComponent,
          ),
      },
    ],
  },
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', component: AdminDashboardComponent },
        { path: 'learning-effectiveness', canActivate: [adminGuard], loadComponent: () => import('./features/admin/investigations/learning-effectiveness-page.component').then(m => m.LearningEffectivenessPageComponent) },
        { path: 'learning-effectiveness/:id', canActivate: [adminGuard], loadComponent: () => import('./features/admin/investigations/learning-effectiveness-page.component').then(m => m.LearningEffectivenessPageComponent) },
        { path: 'submission-investigation/:challengeId/:submissionId', canActivate: [adminGuard], loadComponent: () => import('./features/admin/investigations/submission-investigation-page.component').then(m => m.SubmissionInvestigationPageComponent) },
        { path: 'submission-investigations/:challengeId', canActivate: [adminGuard], loadComponent: () => import('./features/admin/investigations/submission-investigation-list.component').then(m => m.SubmissionInvestigationListComponent) },
      {
        path: 'career-roles',
        component: CareerRoleAdminShellComponent,
        children: [
          { path: '', component: CareerRoleListViewComponent },
          {
            path: ':slug',
            component: CareerRoleDetailComponent,
            children: [
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
              { path: 'overview', component: OverviewTabComponent },
              { path: 'skills', component: SkillsResourcesTabComponent },
              { path: 'evidence-modes', component: TopicEvidenceModesTabComponent },
              { path: 'graph', component: CareerRoleGraphTabComponent },
              { path: 'resources', component: ResourcesRevalidateTabComponent },
              { path: 'coverage', redirectTo: 'skills', pathMatch: 'full' },
            ],
          },
        ],
      },
      {
        path: 'career-challenges',
        component: CareerChallengeAdminShellComponent,
        children: [
          { path: '', component: CareerChallengeListComponent },
          {
            path: 'automation',
            loadComponent: () =>
              import('./features/admin/career-challenges/automation-settings-page/automation-settings-page.component').then(
                (m) => m.OpenChallengeAutomationSettingsPageComponent,
              ),
          },
          {
            path: 'new',
            component: CareerChallengeDetailComponent,
            children: [
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
              {
                path: 'overview',
                component: CareerChallengeOverviewTabComponent,
              },
            ],
          },
          {
            path: ':id',
            component: CareerChallengeDetailComponent,
            children: [
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
              {
                path: 'overview',
                component: CareerChallengeOverviewTabComponent,
              },
              {
                path: 'sponsors',
                component: CareerChallengeSponsorsTabComponent,
              },
              { path: 'judges', component: CareerChallengeJudgesTabComponent },
              { path: 'prizes', component: CareerChallengePrizesTabComponent },
              { path: 'participants', component: CareerChallengeParticipantsTabComponent },
              { path: 'teams', component: CareerChallengeTeamsTabComponent },
              {
                path: 'submissions',
                component: CareerChallengeSubmissionsTabComponent,
              },
              {
                path: 'schedule',
                component: CareerChallengeScheduleTabComponent,
              },
              {
                path: 'preview',
                component: CareerChallengePreviewTabComponent,
              },
            ],
          },
        ],
      },
      {
        path: 'learning-plan-previews',
        loadComponent: () =>
          import('./features/admin/learning-plan-preview-page/learning-plan-preview-page.component').then(
            (m) => m.LearningPlanPreviewPageComponent,
          ),
      },
      {
        path: 'learner-management',
        loadComponent: () =>
          import('./features/admin/account-management-page/account-management-page.component').then(
            (m) => m.AccountManagementPageComponent,
          ),
      },
      {
        path: 'learner-management/:learnerId',
        loadComponent: () =>
          import('./features/admin/account-management-page/account-management-page.component').then(
            (m) => m.AccountManagementPageComponent,
          ),
      },
      {
        path: 'exceptions',
        loadComponent: () =>
          import('./features/admin/exceptions-page/exceptions-page.component').then(
            (m) => m.ExceptionsPageComponent,
          ),
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./features/admin/support-queues-page/support-queues-page.component').then(
            (m) => m.SupportQueuesPageComponent,
          ),
      },
      {
        path: 'skill-deduplication',
        loadComponent: () =>
          import('./features/admin/skill-deduplication-page/skill-deduplication-page.component').then(
            (m) => m.SkillDeDuplicationPageComponent,
          ),
      },
      {
        path: 'archived-skills',
        loadComponent: () =>
          import('./features/admin/archived-skills-page/archived-skills-page.component').then(
            (m) => m.ArchivedSkillsPageComponent,
          ),
      },
      {
        path: 'resource-management',
        loadComponent: () =>
          import('./features/admin/resource-management-page/resource-management-page.component').then(
            (m) => m.ResourceManagementPageComponent,
          ),
      },
      {
        path: 'catalog-collection',
        loadComponent: () =>
          import('./features/admin/catalog-collection-page/catalog-collection-page.component').then(
            (m) => m.CatalogCollectionPageComponent,
          ),
      },
      {
        path: 'zero-resource-sweep',
        loadComponent: () =>
          import('./features/admin/zero-resource-sweep-page/zero-resource-sweep-page.component').then(
            (m) => m.ZeroResourceSweepPageComponent,
          ),
      },
      {
        path: 'topic-dependency-backfill',
        loadComponent: () =>
          import('./features/admin/topic-dependency-backfill-page/topic-dependency-backfill-page.component').then(
            (m) => m.TopicDependencyBackfillPageComponent,
          ),
      },
      {
        path: 'background-jobs',
        loadComponent: () =>
          import('./features/admin/background-jobs-page/background-jobs-page.component').then(
            (m) => m.BackgroundJobsPageComponent,
          ),
      },
      {
        path: 'career-update-sources',
        loadComponent: () =>
          import('./features/admin/career-update-sources-page.component').then(
            (m) => m.CareerUpdateSourcesPageComponent,
          ),
      },
      {
        path: 'role-coverage-report',
        redirectTo: 'career-roles',
        pathMatch: 'full',
      },
      {
        path: 'ai-purpose-routes',
        loadComponent: () =>
          import('./features/admin/ai-purpose-routes-page/ai-purpose-routes-page.component').then(
            (m) => m.AiPurposeRoutesPageComponent,
          ),
      },
      {
        path: 'ai-usage',
        loadComponent: () =>
          import('./features/admin/ai-usage-page/ai-usage-page.component').then(
            (m) => m.AiUsagePageComponent,
          ),
      },
      {
        path: 'performance-diagnostics',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/performance-diagnostics-page/performance-diagnostics-page.component').then(
            (m) => m.PerformanceDiagnosticsPageComponent,
          ),
      },
      {
        path: 'legal-documents',
        loadComponent: () =>
          import('./features/admin/legal-documents-page/legal-documents-page.component').then(
            (m) => m.LegalDocumentsPageComponent,
          ),
      },
      {
        path: 'announcements',
        loadComponent: () =>
          import('./features/admin/announcements-page/announcements-page.component').then(
            (m) => m.AnnouncementsPageComponent,
          ),
      },
      {
        path: 'lessons',
        loadComponent: () =>
          import('./features/admin/lessons-page/lessons-page.component').then(
            (m) => m.LessonsPageComponent,
          ),
      },
    ],
  },
  {
    path: 'career-roles/:slug/full-picture',
    component: AdminLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/admin/career-roles/redirect/full-picture-redirect.component').then(
            (m) => m.FullPictureRedirectComponent,
          ),
      },
    ],
  },
  { path: '**', redirectTo: defaultLandingRedirect },
];
